import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keccak256, stringToBytes } from "viem";
import { useApi } from "../hooks/useApi";
import { useWallet } from "../hooks/useWallet";
import { WalletProviderChooser } from "../components/WalletProviderChooser";
import { ADDRESS_ENV, EXPECTED_CHAIN, EXPECTED_CHAIN_ID_BIGINT, EXPECTED_CHAIN_LABEL, envAddress, envString, requireEnvAddress } from "../utils/config";
import { CODE_APE_NFT_ABI, normalizeMintVoucher, NormalizedMintVoucher } from "../utils/contracts";
import { toImageSrc } from "../utils/ipfs";

type MintPhase = "handoff" | "review" | "done";

interface AgentHandoff {
  imageURI: string;
  imageHash: string;
  metadataURI: string;
  metadataHash: string;
  voucher: Record<string, unknown>;
  signature: string;
  skillVersionHash: string;
  referencePackHash: string;
  agentRunId?: string;
  codexRunId?: string;
}

interface AgentMintPack {
  items: AgentHandoff[];
  agentRunId?: string;
  codexRunId?: string;
}

interface HandoffLookupResponse {
  id: string;
  handoff: unknown;
  expiresAt: string;
}

const SITE_URL = envString("VITE_CODE_APE_SITE_URL") ?? "https://code-ape.com";
const SKILL_URL = envString("VITE_CODE_APE_SKILL_URL") ?? "https://code-ape.com/skill";
const CANONICAL_SKILL_URL = `${SITE_URL.replace(/\/+$/, "")}/skill/RUN.md`;
const CODEX_PROMPT = `Codex, read ${CANONICAL_SKILL_URL} and summon my CODE APE. Use the compact cinematic style lock. Do not copy references or generate flat vector/sticker art.`;

const HANDOFF_EXAMPLE = `{
  "imageURI": "ipfs://...",
  "imageHash": "0x...",
  "metadataURI": "ipfs://...",
  "metadataHash": "0x...",
  "skillVersionHash": "0x...",
  "referencePackHash": "0x...",
  "voucher": {
    "wallet": "0x...",
    "imageHash": "0x...",
    "metadataURIHash": "0x...",
    "metadataHash": "0x...",
    "skillVersionHash": "0x...",
    "referencePackHash": "0x...",
    "price": "3000000000000000",
    "deadline": "1760000000",
    "chainId": "1",
    "nftContract": "0x...",
    "nonce": "1"
  },
  "signature": "0x..."
}`;

export default function MintPage() {
  const api = useApi();
  const { account, connect, switchToExpectedChain, chainId, walletClient, getWalletClient, error: walletError, walletOptions, selectedWalletId, selectWallet } = useWallet();

  const [phase, setPhase] = useState<MintPhase>("handoff");
  const [handoffText, setHandoffText] = useState("");
  const [handoffs, setHandoffs] = useState<AgentHandoff[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [txError, setTxError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [handoffLookupId, setHandoffLookupId] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<`0x${string}` | null>(null);
  const [autoLoadNotice, setAutoLoadNotice] = useState<string | null>(null);

  const nftAddressConfigured = Boolean(envAddress(ADDRESS_ENV.nft));
  const isExpectedChain = chainId === EXPECTED_CHAIN.id;
  const normalizedVouchers = useMemo(() => {
    try { return handoffs.map((item) => normalizeMintVoucher(item.voucher)); } catch { return []; }
  }, [handoffs]);
  const handoff = handoffs[0] ?? null;
  const normalizedVoucher = normalizedVouchers[0] ?? null;
  const packCount = handoffs.length;
  const isPack = packCount > 1;
  const totalMintValue = normalizedVouchers.reduce((sum, voucher) => sum + voucher.price, 0n);

  useEffect(() => {
    const handoffId = readHandoffIdFromSearch();
    if (handoffId) {
      setHandoffLookupId(handoffId);
      setHandoffLoading(true);
      setParseError(null);
      api.get<HandoffLookupResponse>(`/handoff/${encodeURIComponent(handoffId)}`)
        .then((response) => {
          const raw = JSON.stringify(response.handoff);
          setHandoffText(raw);
          loadHandoff(raw, `Mint session ${response.id} loaded. Review the Ape, choose a wallet, then approve the mint. Link expires ${new Date(response.expiresAt).toLocaleString()}.`);
        })
        .catch((e: unknown) => {
          setParseError(friendlyHandoffLoadError(e));
        })
        .finally(() => {
          setHandoffLoading(false);
        });
      return;
    }

    const encoded = readHandoffFromHash();
    if (!encoded) return;
    try {
      const raw = decodeHandoffPayload(encoded);
      setHandoffText(raw);
      loadHandoff(raw, "Mint link loaded. Review the Ape, choose a wallet, then approve the mint.");
      window.history.replaceState(null, "", window.location.pathname);
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : "Could not read the mint link.");
    }
  // Run once on page load. The URL is a transport envelope, not app state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function copyPrompt() {
    await navigator.clipboard?.writeText(CODEX_PROMPT);
  }

  function parseHandoff() {
    loadHandoff(handoffText);
  }

  function loadHandoff(raw: string, notice?: string) {
    setParseError(null);
    setTxError(null);
    setAutoLoadNotice(notice ?? null);
    try {
      const parsed = JSON.parse(raw) as unknown;
      const items = readHandoffItems(parsed);
      if (items.length === 0 || items.length > 10) throw new Error("Mint pack must contain 1 to 10 Apes.");

      const configuredNftAddress = envAddress(ADDRESS_ENV.nft);
      for (const [index, item] of items.entries()) {
        const prefix = items.length > 1 ? `Item ${index + 1}: ` : "";
        if ("imageSrc" in (item as unknown as Record<string, unknown>)) {
          throw new Error(`${prefix}handoff packet must use canonical imageURI, not inline imageSrc data.`);
        }
        if (!item.imageURI || !item.imageHash || !item.metadataURI || !item.metadataHash || !item.skillVersionHash || !item.referencePackHash || !item.voucher || !item.signature) {
          throw new Error(`${prefix}handoff packet must include imageURI, imageHash, metadataURI, metadataHash, skillVersionHash, referencePackHash, voucher, and signature.`);
        }
        const normalized = normalizeMintVoucher(item.voucher);
        if (normalized.chainId !== EXPECTED_CHAIN_ID_BIGINT) throw new Error(`${prefix}voucher must target ${EXPECTED_CHAIN_LABEL} chainId ${EXPECTED_CHAIN.id}.`);
        if (configuredNftAddress && normalized.nftContract.toLowerCase() !== configuredNftAddress.toLowerCase()) {
          throw new Error(`${prefix}voucher does not target the configured CODE APE NFT contract.`);
        }
        assertSameHash(`${prefix}imageHash`, item.imageHash, normalized.imageHash);
        assertSameHash(`${prefix}metadataHash`, item.metadataHash, normalized.metadataHash);
        assertSameHash(`${prefix}skillVersionHash`, item.skillVersionHash, normalized.skillVersionHash);
        assertSameHash(`${prefix}referencePackHash`, item.referencePackHash, normalized.referencePackHash);
        const computedMetadataURIHash = keccak256(stringToBytes(item.metadataURI));
        assertSameHash(`${prefix}metadataURIHash`, computedMetadataURIHash, normalized.metadataURIHash);
      }
      setHandoffs(items);
      setPhase("review");
    } catch (e: unknown) {
      setHandoffs([]);
      setAutoLoadNotice(null);
      setParseError(e instanceof Error ? e.message : "Invalid AI-agent handoff packet.");
    }
  }

  async function ensureConnected(): Promise<`0x${string}` | null> {
    if (account) return account;
    return connect();
  }

  async function handleMint() {
    if (!handoffs.length || !normalizedVoucher || normalizedVouchers.length !== handoffs.length) return;
    setTxError(null);
    setBusy(true);
    try {
      const connected = await ensureConnected();
      if (!connected) throw new Error("Connect a wallet before minting.");
      if (chainId !== EXPECTED_CHAIN.id) {
        const switched = await switchToExpectedChain();
        if (!switched) throw new Error(`Switch to ${EXPECTED_CHAIN_LABEL} before minting.`);
      }
      const activeWalletClient = walletClient ?? getWalletClient(connected);
      if (!activeWalletClient) throw new Error("Wallet client is not ready. Reopen this page in your wallet browser and try again.");
      const nftAddress = requireEnvAddress(ADDRESS_ENV.nft);
      for (const voucher of normalizedVouchers) {
        if (voucher.wallet.toLowerCase() !== connected.toLowerCase()) {
          throw new Error("Voucher wallet does not match the connected account.");
        }
        if (voucher.nftContract.toLowerCase() !== nftAddress.toLowerCase()) {
          throw new Error("Voucher does not target the configured CODE APE NFT contract.");
        }
      }
      const tx = isPack
        ? await activeWalletClient.writeContract({
            address: nftAddress,
            abi: CODE_APE_NFT_ABI,
            functionName: "mintManyWithVouchers",
            args: [
              normalizedVouchers,
              handoffs.map((item) => item.metadataURI),
              handoffs.map((item) => item.signature as `0x${string}`)
            ],
            account: connected,
            chain: EXPECTED_CHAIN,
            value: totalMintValue
          })
        : await activeWalletClient.writeContract({
            address: nftAddress,
            abi: CODE_APE_NFT_ABI,
            functionName: "mintWithVoucher",
            args: [normalizedVoucher, handoff.metadataURI, handoff.signature as `0x${string}`],
            account: connected,
            chain: EXPECTED_CHAIN,
            value: normalizedVoucher.price
          });
      setTxHash(tx);
      setPhase("done");
    } catch (e: unknown) {
      setTxError(friendlyMintError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <div className="container" style={{ paddingBottom: "var(--space-24)" }}>
        <div className="mint-layout">
          <aside className="card" style={{ position: "sticky", top: "calc(var(--nav-height) + var(--space-6))", padding: "var(--space-5)" }}>
            <div className="badge badge-prime" style={{ marginBottom: "var(--space-4)" }}>STEP 2 OF 2</div>
            <h1 className="text-4xl" style={{ marginBottom: "var(--space-4)" }}>Sign your summoned Ape</h1>
            <p className="text-muted" style={{ lineHeight: 1.7, marginBottom: "var(--space-5)" }}>
              Your AI-agent handoff is ready. Review the mint session, choose All wallets, WalletConnect, or your browser wallet, then approve the final mint transaction.
            </p>
            <div className="alert alert-info" style={{ marginBottom: "var(--space-5)" }}>
              <span>🤖</span>
              <div>
                <strong>Need a mint link?</strong>
                <pre className="text-mono text-xs" style={{ whiteSpace: "pre-wrap", marginTop: "var(--space-2)", color: "var(--text-primary)" }}>{CODEX_PROMPT}</pre>
              </div>
            </div>
            <div style={{ display: "grid", gap: "var(--space-3)", marginBottom: "var(--space-5)" }}>
              <a href={SKILL_URL} className="btn btn-primary" target="_blank" rel="noreferrer" id="mint-open-skill-btn">🚀 Start with Codex →</a>
              <button className="btn btn-secondary" onClick={copyPrompt} id="mint-copy-prompt-btn">📋 Copy the Summon Spell</button>
              <Link to="/gallery" className="btn btn-secondary" id="mint-open-gallery-btn">NFT Gallery / Details</Link>
            </div>
            <div className="dense-kv">
              <InfoRow label="Source" value="Public Skill" />
              <InfoRow label="Mint price" value="0.003 ETH" />
              <InfoRow label="Voucher" value="EIP-712" />
              <InfoRow label="Image" value="IPFS + hash" />
            </div>
          </aside>

          <section>
            {walletError && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{walletError}</div>}
            {!nftAddressConfigured && (
              <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>
                Contract writes disabled: {ADDRESS_ENV.nft} is not configured.
              </div>
            )}

            {phase === "handoff" && (
              <div className="card">
                <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-4)", alignItems: "flex-start", marginBottom: "var(--space-5)" }}>
                  <div>
                    <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>{handoffLookupId ? "Step 1 / loading mint link" : "Start with an AI-agent mint link"}</div>
                    <h2 className="text-2xl">{handoffLookupId ? "Loading your mint session" : "Create your mint session with Codex or a compatible agent"}</h2>
                  </div>
                </div>
                {handoffLoading && (
                  <div className="alert alert-info" style={{ marginBottom: "var(--space-4)" }}>
                    <span className="spinner" />
                    <div>Loading mint session {handoffLookupId}. No wallet transaction is needed until the review step.</div>
                  </div>
                )}
                {!handoffLoading && !handoffLookupId && (
                  <div className="alert alert-info" style={{ marginBottom: "var(--space-4)" }}>
                    <span>1</span>
                    <div>
                      <strong>Authorize an AI agent, then open the link it returns.</strong>
                      <div className="text-sm text-muted" style={{ marginTop: 6 }}>
                        Codex or a compatible agent creates the image, pins metadata, requests the signed voucher, stores the mint session, and gives you a short link for this page.
                      </div>
                      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", marginTop: "var(--space-4)" }}>
                        <Link className="btn btn-primary" to="/agent-auth">AI Agent Auth</Link>
                        <a className="btn btn-secondary" href={SKILL_URL} target="_blank" rel="noreferrer">Open Summon Guide</a>
                      </div>
                    </div>
                  </div>
                )}
                {parseError && <div className="alert alert-error" style={{ marginTop: "var(--space-4)" }}>{parseError}</div>}
                <details style={{ marginTop: "var(--space-5)" }}>
                  <summary className="text-sm text-muted" style={{ cursor: "pointer" }}>Advanced: load a handoff packet</summary>
                  <div style={{ marginTop: "var(--space-4)" }}>
                    <textarea
                      className="input text-mono"
                      id="codex-handoff-json"
                      rows={18}
                      placeholder={HANDOFF_EXAMPLE}
                      value={handoffText}
                      onChange={(e) => setHandoffText(e.target.value)}
                      disabled={handoffLoading}
                      style={{ resize: "vertical", lineHeight: 1.55, fontSize: "var(--font-sm)", minHeight: 320 }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-4)", marginTop: "var(--space-4)", flexWrap: "wrap" }}>
                      <p className="text-sm text-muted" style={{ maxWidth: 620 }}>
                        A short mint link is the standard path. This advanced loader verifies the same signed session before any wallet transaction.
                      </p>
                      <button className="btn btn-primary" onClick={parseHandoff} disabled={!handoffText.trim()} id="parse-codex-handoff-btn">Review your Ape →</button>
                    </div>
                  </div>
                </details>
              </div>
            )}

            {phase === "review" && handoff && normalizedVoucher && normalizedVouchers.length === handoffs.length && (
              <div className="card">
                <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-4)", alignItems: "flex-start", marginBottom: "var(--space-5)" }}>
                  <div>
                    <div className="badge badge-gold" style={{ marginBottom: "var(--space-3)" }}>Step 2 / review & mint</div>
                    <h2 className="text-2xl">Meet your Ape</h2>
                  </div>
                  <button className="btn btn-sm btn-secondary" onClick={() => setPhase("handoff")}>← Load another session</button>
                </div>

                {autoLoadNotice && <div className="alert alert-info" style={{ marginBottom: "var(--space-4)" }}>{autoLoadNotice}</div>}

                <div className="card" style={{ padding: "var(--space-5)", marginBottom: "var(--space-5)", background: "rgba(0,255,65,0.07)" }}>
                  <div className="badge badge-prime" style={{ marginBottom: "var(--space-3)" }}>Ready to mint</div>
                  <h3 className="text-2xl" style={{ marginBottom: "var(--space-2)" }}>
                    {isPack ? `${packCount} CODE APEs are ready` : "Your CODE APE is ready"}
                  </h3>
                  <p className="text-sm text-muted" style={{ lineHeight: 1.7, marginBottom: "var(--space-4)" }}>
                    One click here opens wallet selection, checks Ethereum mainnet, verifies the voucher, and asks your wallet for the final mint transaction. The AI-agent handoff has already handled image generation and voucher signing.
                  </p>
                  {txError && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{txError}</div>}
                  {!account && <WalletProviderChooser walletOptions={walletOptions} selectedWalletId={selectedWalletId} selectWallet={selectWallet} />}
                  {!account && walletOptions.length === 0 && <MobileWalletLinks />}
                  <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center", marginTop: walletOptions.length > 1 ? "var(--space-4)" : 0 }}>
                    <button className="btn btn-primary btn-lg" onClick={handleMint} disabled={busy || !nftAddressConfigured} id="btn-mint" style={{ justifyContent: "center" }}>
                      {busy ? <><span className="spinner" /> Minting…</> : mintButtonLabel({ account, isExpectedChain, isPack, packCount, totalMintValue })}
                    </button>
                    {account && !isExpectedChain && (
                      <span className="text-sm text-muted">Your wallet will be asked to switch to {EXPECTED_CHAIN_LABEL} first.</span>
                    )}
                  </div>
                </div>

                <div className="mint-review-grid">
                  <div className="nft-preview-panel">
                    {handoff.imageURI ? (
                      <img src={toImageSrc(handoff.imageURI)} alt="AI-agent generated CODE APE" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "var(--radius)", border: "1px solid var(--color-border)" }} />
                    ) : (
                      <div className="nft-card__image-placeholder" style={{ borderRadius: "var(--radius)", border: "1px solid var(--color-border)" }}><span>🦍</span></div>
                    )}
                    <div className="badge badge-green" style={{ marginTop: "var(--space-3)" }}>{isPack ? `${packCount} Ape mint pack loaded` : "AI-agent handoff loaded"}</div>
                  </div>

                  <div className="dense-kv">
                    {isPack && <InfoRow label="Pack size" value={`${packCount} Apes`} />}
                    <InfoRow label={isPack ? "First nonce" : "Voucher nonce"} value={normalizedVoucher.nonce.toString()} />
                    <InfoRow label="Wallet" value={shorten(normalizedVoucher.wallet)} />
                    <InfoRow label="NFT contract" value={shorten(normalizedVoucher.nftContract)} />
                    <InfoRow label={isPack ? "Total price" : "Price"} value={`${formatEth(totalMintValue)} ETH`} />
                    <InfoRow label="Voucher expires" value={new Date(Number(normalizedVoucher.deadline) * 1000).toLocaleString()} />
                    <InfoRow label="Image hash" value={shorten(handoff.imageHash)} />
                    <InfoRow label="Metadata hash" value={shorten(handoff.metadataHash)} />
                    <InfoRow label="Metadata URI" value={handoff.metadataURI} />
                    <InfoRow label="Skill hash" value={shorten(handoff.skillVersionHash)} />
                    <InfoRow label="Reference pack" value={shorten(handoff.referencePackHash)} />
                  </div>
                </div>

                <div className="alert alert-warning" style={{ marginTop: "var(--space-5)", marginBottom: "var(--space-4)" }}>
                  <span>⚠️</span>
                  <div>
                    Once minting is complete, no admin can pause it. Pending WETH belongs to whoever owns the NFT right now, so claim before listing on third-party marketplaces.
                  </div>
                </div>
              </div>
            )}

            {phase === "done" && (
              <div className="card text-center">
                <div style={{ fontSize: "4rem", marginBottom: "var(--space-4)" }}>🦍</div>
                <h2 className="text-2xl" style={{ marginBottom: "var(--space-3)" }}>Welcome to the colony.</h2>
                <p className="success-subtitle">You did not mint a JPEG. You collected evidence that an AI drew it.</p>
                <p className="text-muted" style={{ marginBottom: "var(--space-5)" }}>{isPack ? "Your Apes now earn WETH from every official swap. You can claim fees any time, or burn later for CAPE." : "Your Ape now earns WETH from every official swap. You can claim fees any time, or burn later for CAPE."}</p>
                {txHash && <p className="text-mono text-sm" style={{ color: "var(--color-prime)", marginBottom: "var(--space-5)", overflowWrap: "anywhere" }}>Tx: {txHash}</p>}
                <div style={{ display: "flex", gap: "var(--space-4)", justifyContent: "center", flexWrap: "wrap" }}>
                  <Link to="/gallery" className="btn btn-secondary">Open NFT Gallery</Link>
                  <Link to="/fees" className="btn btn-primary">Claim WETH Fees →</Link>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

function readHandoffIdFromSearch(): string | null {
  const params = new URLSearchParams(window.location.search);
  const id = params.get("h") || params.get("handoffId");
  return id ? id.trim().toUpperCase() : null;
}

function readHandoffFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  return params.get("handoff") || params.get("codeape") || params.get("handoffJson");
}

function decodeHandoffPayload(value: string): string {
  const decoded = decodeURIComponent(value.trim());
  if (decoded.startsWith("{")) return decoded;
  const base64 = decoded.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const text = new TextDecoder().decode(bytes);
  if (!text.trim().startsWith("{")) throw new Error("Mint link did not contain a handoff packet.");
  return text;
}

function readHandoffItems(parsed: unknown): AgentHandoff[] {
  if (Array.isArray(parsed)) return parsed.map(requireHandoffObject);
  if (isRecord(parsed) && Array.isArray((parsed as Partial<AgentMintPack>).items)) {
    return (parsed as Partial<AgentMintPack>).items!.map(requireHandoffObject);
  }
  return [requireHandoffObject(parsed)];
}

function requireHandoffObject(value: unknown): AgentHandoff {
  if (!isRecord(value)) throw new Error("Handoff packet must be an object or a mint pack with items.");
  return value as unknown as AgentHandoff;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assertSameHash(field: string, left: string, right: string) {
  if (left.toLowerCase() !== right.toLowerCase()) {
    throw new Error(`Handoff ${field} does not match voucher.${field}.`);
  }
}

function MobileWalletLinks() {
  const links = mobileWalletLinks();
  return (
    <div className="alert alert-info" style={{ marginBottom: "var(--space-4)", alignItems: "flex-start" }}>
      <span>📱</span>
      <div>
        <strong>No wallet detected in this browser.</strong>
        <p className="text-sm" style={{ marginTop: "var(--space-2)", marginBottom: "var(--space-3)", lineHeight: 1.65 }}>
          On mobile, open this same mint link inside a wallet browser, then tap the mint button again.
        </p>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {links.map((link) => (
            <a key={link.label} className="btn btn-sm btn-secondary" href={link.href} rel="noreferrer">
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function mobileWalletLinks(): Array<{ label: string; href: string }> {
  const currentUrl = window.location.href;
  const encodedUrl = encodeURIComponent(currentUrl);
  const dappPath = currentUrl.replace(/^https?:\/\//, "");
  return [
    { label: "Open in MetaMask", href: `https://metamask.app.link/dapp/${dappPath}` },
    { label: "Open in Coinbase Wallet", href: `https://go.cb-w.com/dapp?cb_url=${encodedUrl}` },
    { label: "Open in Trust Wallet", href: `https://link.trustwallet.com/open_url?coin_id=60&url=${encodedUrl}` }
  ];
}

function InfoRow({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="kv-row">
      <span>{label}</span>
      <strong style={{ color: danger ? "var(--color-red)" : undefined }}>{value}</strong>
    </div>
  );
}

function shorten(value: string): string {
  if (!value) return "—";
  return value.length > 24 ? `${value.slice(0, 12)}…${value.slice(-8)}` : value;
}

function formatEth(wei: bigint): string {
  const scaled = wei * 1000000n / 10n ** 18n;
  return (Number(scaled) / 1000000).toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
}

function friendlyHandoffLoadError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/expired/i.test(message)) return "This mint link expired. Ask Codex or your compatible agent to create a fresh mint link.";
  if (/not found|invalid/i.test(message)) return "This mint link is invalid or no longer exists. Ask Codex or your compatible agent to create a fresh mint link.";
  if (/not configured|backend api/i.test(message)) return "Mint session loading is unavailable because the backend API is not configured.";
  return "Could not load this mint link. Ask Codex or your compatible agent to return a fresh short mint link.";
}

function friendlyMintError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/user rejected|rejected the request|denied|4001/i.test(message)) return "Wallet request was rejected. Click mint again when you are ready.";
  if (/wallet.*match|account/i.test(message)) return "Wallet mismatch: connect the same wallet that authorized this AI-agent mint session.";
  if (/chain|network|switch/i.test(message)) return `Wrong network: switch your wallet to ${EXPECTED_CHAIN_LABEL} and try again.`;
  if (/expired|deadline/i.test(message)) return "Voucher expired. Ask Codex or your compatible agent to create a fresh mint link.";
  if (/insufficient funds|exceeds balance/i.test(message)) return "Insufficient ETH for the mint price plus gas.";
  if (/not ready|no wallet|connect/i.test(message)) return "No wallet is ready. Choose All wallets, use WalletConnect, or unlock your browser wallet, then try again.";
  return message || "Mint failed.";
}

function mintButtonLabel(input: {
  account: `0x${string}` | null;
  isExpectedChain: boolean;
  isPack: boolean;
  packCount: number;
  totalMintValue: bigint;
}) {
  if (!input.account) return input.isPack ? `Connect & Mint ${input.packCount} Apes — ${formatEth(input.totalMintValue)} ETH` : "Connect & Mint your Ape — 0.003 ETH";
  if (!input.isExpectedChain) return `Switch & Mint on ${EXPECTED_CHAIN_LABEL}`;
  return input.isPack ? `Sign & Mint ${input.packCount} Apes — ${formatEth(input.totalMintValue)} ETH` : "Sign & Mint your Ape — 0.003 ETH";
}
